import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Send, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const { sendContactMessage } = await import('@/utils/api');
      await sendContactMessage(formData);
      
      toast.success('Message envoyé ! Nous vous répondrons dans les plus brefs délais.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="relative z-10 text-center text-white px-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="text-[#c9a961]" size={24} />
            <span className="text-[#c9a961] font-medium tracking-wider uppercase">Contactez-nous</span>
          </div>
          <h1 className="hero-title text-5xl md:text-7xl mb-4">Contact</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Une question ? Nous sommes là pour vous répondre
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-[#f8f7f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="section-title text-3xl md:text-4xl mb-6 text-[#1a1a1a] gold-line pb-4">
                  Informations de contact
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  N'hésitez pas à nous contacter pour toute question ou demande de renseignements. Notre équipe se fera un plaisir de vous répondre.
                </p>
              </div>

              <div className="space-y-6">
                {/* Address */}
                <Card className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid="contact-address-card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#c9a961]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Adresse</h3>
                        <p className="text-gray-600">Montmagny et Courbevoie, France</p>
                        <p className="text-sm text-gray-500 mt-2">Emplacement exact communiqué lors de la réservation</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Phone */}
                <Card className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid="contact-phone-card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#c9a961]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Téléphone</h3>
                        <a
                          href="tel:0650470940"
                          className="text-[#c9a961] hover:text-[#b8984e] transition-colors text-lg font-medium"
                        >
                          06 50 47 09 40
                        </a>
                        <p className="text-sm text-gray-500 mt-2">Lun - Sam: 9h00 - 19h00</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Email */}
                <Card className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid="contact-email-card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#c9a961]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Email</h3>
                        <a
                          href="mailto:contact.sukiauto@gmail.com"
                          className="text-[#c9a961] hover:text-[#b8984e] transition-colors break-all"
                        >
                          contact.sukiauto@gmail.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Hours */}
                <Card className="border-none shadow-lg hover:shadow-xl transition-shadow" data-testid="contact-hours-card">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="text-[#c9a961]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Horaires d'ouverture</h3>
                        <div className="space-y-1 text-gray-600">
                          <p>Lundi - Samedi: 9h00 - 19h00</p>
                          <p>Dimanche: Sur rendez-vous</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Suivez-nous</h3>
                <div className="flex space-x-4">
                  <a
                    href="https://www.instagram.com/suki_paris?igsh=MWw0MnBtYW94ZzY3Mg%3D%3D&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#c9a961] flex items-center justify-center hover:bg-[#b8984e] transition-all duration-300 text-white"
                    data-testid="contact-instagram-link"
                  >
                    <Instagram size={24} />
                  </a>
                  <a
                    href="https://www.facebook.com/share/17XwxddMzm/?mibextid=wwXIfr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#c9a961] flex items-center justify-center hover:bg-[#b8984e] transition-all duration-300 text-white"
                    data-testid="contact-facebook-link"
                  >
                    <Facebook size={24} />
                  </a>
                  <a
                    href="https://www.tiktok.com/@_suki_paris?_t=ZN-90zF8hGaV2U&_r=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#c9a961] flex items-center justify-center hover:bg-[#b8984e] transition-all duration-300 text-white"
                    data-testid="contact-tiktok-link"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="shadow-2xl border-none">
                <CardContent className="p-8">
                  <h2 className="section-title text-3xl mb-6 text-[#1a1a1a]">Envoyez-nous un message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">Nom complet *</Label>
                        <Input
                          id="contact-name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Jean Dupont"
                          required
                          data-testid="contact-name-input"
                          className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="jean.dupont@email.com"
                          required
                          data-testid="contact-email-input"
                          className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="contact-phone">Téléphone</Label>
                        <Input
                          id="contact-phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="06 12 34 56 78"
                          data-testid="contact-phone-input"
                          className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="contact-subject">Sujet</Label>
                        <Input
                          id="contact-subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="Demande de renseignements"
                          data-testid="contact-subject-input"
                          className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Votre message..."
                        rows={6}
                        required
                        data-testid="contact-message-textarea"
                        className="border-gray-300 focus:border-[#c9a961] focus:ring-[#c9a961]"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit-button"
                      className="w-full bg-[#c9a961] hover:bg-[#b8984e] text-white text-lg py-6 rounded-full font-semibold transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <span>Envoyer le message</span>
                      <Send size={20} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title text-3xl md:text-4xl mb-4 text-[#1a1a1a] gold-line inline-block pb-4">
              Notre Localisation
            </h2>
            <p className="text-gray-600">Nous sommes situés à Montmagny et Courbevoie</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2612.8!2d0.6886!3d49.1614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMontmagny%2C%20France!5e0!3m2!1sfr!2sfr!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SUKI Montmagny Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
